import { useEffect, useState, createContext, useContext } from "react";
import axios from "axios";
import { Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ensure Judge.me CSS is loaded once
function ensureJudgeMeStyle() {
    if (typeof document === "undefined") return;
    if (document.getElementById("judgeme-css")) return;
    const link = document.createElement("link");
    link.id = "judgeme-css";
    link.rel = "stylesheet";
    link.href = "https://cdn.judge.me/assets/installed.css";
    document.head.appendChild(link);
}

// Shared context so <JudgeMeSummary> and <JudgeMeSection> use ONE fetch per page
const JudgeMeContext = createContext(null);

export function JudgeMeProvider({ externalId, handle, children }) {
    const [state, setState] = useState({
        loading: !!externalId,
        widget: "",
        avg: null,
        count: 0,
        error: null,
    });

    useEffect(() => {
        let cancelled = false;
        if (!externalId) {
            setState({ loading: false, widget: "", avg: null, count: 0, error: null });
            return () => {};
        }
        setState((s) => ({ ...s, loading: true, error: null }));
        const params = handle ? { params: { handle } } : {};
        axios
            .get(`${API}/reviews/judgeme/${externalId}`, params)
            .then((r) => {
                if (cancelled) return;
                setState({
                    loading: false,
                    widget: r.data?.widget || "",
                    avg: r.data?.avg_rating ?? null,
                    count: Number(r.data?.count || 0),
                    error: null,
                });
            })
            .catch((e) => {
                if (cancelled) return;
                setState({
                    loading: false,
                    widget: "",
                    avg: null,
                    count: 0,
                    error: e?.response?.data?.detail || "Errore caricamento recensioni",
                });
            });
        return () => {
            cancelled = true;
        };
    }, [externalId, handle]);

    return (
        <JudgeMeContext.Provider value={state}>{children}</JudgeMeContext.Provider>
    );
}

function useJudgeMe() {
    return (
        useContext(JudgeMeContext) || {
            loading: false,
            widget: "",
            avg: null,
            count: 0,
            error: null,
        }
    );
}

// ------ Inline summary (next to product title) ------

export function JudgeMeSummary({ className = "" }) {
    const { loading, avg, count } = useJudgeMe();
    if (loading || !count || !avg) return null;
    const full = Math.floor(avg);
    const half = avg - full >= 0.5;
    const stars = [];
    for (let i = 0; i < 5; i++) {
        const active = i < full || (i === full && half);
        stars.push(
            <Star
                key={i}
                size={16}
                strokeWidth={2}
                className={
                    active ? "fill-bounty text-bounty" : "fill-none text-ink/30"
                }
            />
        );
    }
    return (
        <div
            className={`inline-flex items-center gap-2 ${className}`}
            data-testid="judgeme-summary"
            aria-label={`Valutazione ${avg.toFixed(2)} su 5 basata su ${count} recensioni`}
        >
            <div className="flex items-center gap-0.5">{stars}</div>
            <span className="font-mono text-xs text-ink/70">
                {avg.toFixed(2)} · {count} {count === 1 ? "recensione" : "recensioni"}
            </span>
        </div>
    );
}

// ------ Full widget (below description) ------

export function JudgeMeSection() {
    const { loading, widget, count, error } = useJudgeMe();

    useEffect(() => {
        ensureJudgeMeStyle();
    }, []);

    if (loading) {
        return (
            <section
                className="max-w-4xl mx-auto mt-12"
                data-testid="judgeme-section-loading"
            >
                <div className="font-mono text-xs text-ink/50 tracking-widest">
                    caricamento recensioni…
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section
                className="max-w-4xl mx-auto mt-12"
                data-testid="judgeme-section-error"
            >
                <div className="font-mono text-xs text-ink/50">
                    recensioni non disponibili ora
                </div>
            </section>
        );
    }

    return (
        <section
            className="max-w-4xl mx-auto mt-12"
            data-testid="judgeme-section"
        >
            <div className="font-hand text-lg text-bounty dashed-underline inline-block mb-1">
                - dagli altri pirati -
            </div>
            <h2 className="font-display text-3xl tracking-tight leading-none mb-4">
                RECENSIONI DEI CLIENTI
            </h2>

            {count === 0 ? (
                <div
                    className="bounty-card p-6 text-center font-hand text-ink/60"
                    data-testid="judgeme-empty"
                >
                    Nessuna recensione ancora — sii il primo a lasciare la tua
                    valutazione su Shopify.
                </div>
            ) : (
                <div className="bounty-card p-4 lg:p-6">
                    <div
                        className="judgeme-widget-wrapper"
                        data-testid="judgeme-widget"
                        dangerouslySetInnerHTML={{ __html: widget }}
                    />
                </div>
            )}
        </section>
    );
}
